import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import api from '../services/api';

const AuthContext = createContext();

const initialState = {
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: false,
  isLoading: true,
};

const authReducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN_START':
      return { ...state, isLoading: true };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
      };
    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };
    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const navigate = useNavigate();

  // Set auth token in API headers
  useEffect(() => {
    if (state.token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${state.token}`;
    } else {
      delete api.defaults.headers.common['Authorization'];
    }
  }, [state.token]);

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      if (state.token) {
        try {
          const response = await api.get('/api/auth/profile');
          dispatch({
            type: 'LOGIN_SUCCESS',
            payload: { user: response.data.user, token: state.token },
          });
        } catch (error) {
          localStorage.removeItem('token');
          dispatch({ type: 'LOGIN_FAILURE' });
        }
      } else {
        dispatch({ type: 'LOGIN_FAILURE' });
      }
    };

    checkAuth();
  }, []);

  const login = async (email, password, redirectTo) => {
    dispatch({ type: 'LOGIN_START' });
    
    try {
      const response = await api.post('/api/auth/login', { email, password });
      const { user, token } = response.data;
      
      localStorage.setItem('token', token);
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user, token },
      });
      
      toast.success('Login successful!');
      // Only allow internal (relative) redirects to avoid open-redirect issues
      const safeRedirect =
        typeof redirectTo === 'string' && redirectTo.startsWith('/') && !redirectTo.startsWith('//')
          ? redirectTo
          : '/dashboard';
      navigate(safeRedirect, { replace: true });
      return { success: true };
    } catch (error) {
      // Check if email is not verified
      if (error.response?.data?.requiresVerification) {
        const userEmail = error.response?.data?.email || email;
        dispatch({ type: 'LOGIN_FAILURE' });
        // Navigate to verification page
        navigate(`/verify-email?email=${encodeURIComponent(userEmail)}`, { replace: true });
        return { success: false, requiresVerification: true };
      }
      
      const message = error.response?.data?.error || 'Login failed';
      toast.error(message);
      dispatch({ type: 'LOGIN_FAILURE' });
      return { success: false };
    }
  };

  const register = async (userData, redirectTo) => {
    dispatch({ type: 'LOGIN_START' });
    
    try {
      const response = await api.post('/api/auth/register', userData);
      
      // Registration now requires email verification
      dispatch({ type: 'LOGIN_FAILURE' });
      toast.success(response.data.message || 'Registration successful! Please check your email to verify your account.');
      // Preserve an internal post-verification redirect (e.g. /cart for carts
      // built by an admin) through the verification step.
      const safeRedirect =
        typeof redirectTo === 'string' && redirectTo.startsWith('/') && !redirectTo.startsWith('//')
          ? redirectTo
          : null;
      const verifyUrl =
        `/verify-email?email=${encodeURIComponent(userData.email)}` +
        (safeRedirect ? `&redirect=${encodeURIComponent(safeRedirect)}` : '');
      navigate(verifyUrl);
    } catch (error) {
      const message = error.response?.data?.error || 'Registration failed';
      toast.error(message);
      dispatch({ type: 'LOGIN_FAILURE' });
    }
  };

  // Set auth state manually (used after email verification)
  const setAuthState = (user, token) => {
    localStorage.setItem('token', token);
    dispatch({
      type: 'LOGIN_SUCCESS',
      payload: { user, token },
    });
  };

  const logout = () => {
    localStorage.removeItem('token');
    dispatch({ type: 'LOGOUT' });
    toast.success('Logged out successfully');
    navigate('/');
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await api.put('/api/auth/profile', profileData);
      dispatch({
        type: 'UPDATE_USER',
        payload: response.data.user,
      });
      toast.success('Profile updated successfully');
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to update profile';
      toast.error(message);
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await api.put('/api/auth/change-password', {
        currentPassword,
        newPassword,
      });
      toast.success('Password changed successfully');
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to change password';
      toast.error(message);
    }
  };

  const value = {
    ...state,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    setAuthState,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 